/**
 * ServiceWorkerRegistrar — компонент для управління lifecycle Service Worker
 *
 * Використовує нативний navigator.serviceWorker API.
 * VitePWA з injectRegister: 'script' сам реєструє SW через <script> тег.
 * Цей компонент відстежує події оновлення і синхронізує їх з MobX PwaManager.
 * Також підписує браузер на Web Push (VAPID) після реєстрації SW.
 *
 * Не залежить від virtual:pwa-register — працює в dev і production.
 */

import { reaction } from 'mobx'
import { useEffect } from 'react'

import { restClient } from '@/Core'
import { userManager } from '@/Core/Managers'

import { savePushCredentials, clearPushCredentials } from './PushCredentialsStore'
import { pwaManager } from './PwaManager'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

interface ServiceWorkerRegistrarProps {
    /** Інтервал перевірки оновлень SW (мс). За замовчуванням — 60 хвилин */
    updateCheckInterval?: number
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = atob(base64)
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)))
}

/**
 * Чи підписана наявна підписка саме поточним VAPID-ключем.
 *
 * Якщо ключ на сервері змінили, стара підписка стає непридатною: сервер
 * підписує повідомлення новим ключем, а браузер чекає на старий, і push
 * мовчки не доходить. Тому таку підписку треба перестворити.
 *
 * Коли браузер не віддає applicationServerKey (options не підтримується),
 * вважаємо підписку валідною — краще лишити робочу, ніж перепідписувати
 * на кожному запуску.
 */
export function subscriptionMatchesKey(subscription: PushSubscription, vapidPublicKey: string): boolean {
    const current = subscription.options?.applicationServerKey
    if (!current) return true

    const expected = urlBase64ToUint8Array(vapidPublicKey)
    const actual = new Uint8Array(current as ArrayBuffer)

    if (actual.length !== expected.length) return false
    return actual.every((byte, i) => byte === expected[i])
}

export async function subscribeToPush(registration: ServiceWorkerRegistration): Promise<void> {
    if (!VAPID_PUBLIC_KEY) {
        console.warn('[WebPush] VITE_VAPID_PUBLIC_KEY is empty, push disabled')
        return
    }
    if (!('PushManager' in window)) {
        console.warn('[WebPush] PushManager unsupported in this browser')
        return
    }

    // Даємо SW змогу самостійно перепідписатись при ротації endpoint,
    // коли сторінки застосунку може не бути (див. sw.ts).
    if (userManager.token) {
        await savePushCredentials({ vapidPublicKey: VAPID_PUBLIC_KEY, token: userManager.token })
    }

    try {
        const existing = await registration.pushManager.getSubscription()
        if (existing) {
            // Звіряємо ключ підписки з поточним VAPID: після ротації ключа
            // стара підписка назавжди лишалась би мертвою, бо ми просто
            // перевикористовували її, не перевіряючи, чим вона підписана.
            if (subscriptionMatchesKey(existing, VAPID_PUBLIC_KEY)) {
                console.log('[WebPush] reusing existing subscription', existing.endpoint)
                await sendSubscriptionToServer(existing)
                return
            }

            console.warn('[WebPush] VAPID key changed, re-subscribing')
            await existing.unsubscribe().catch(() => {
                // Не критично: subscribe() нижче все одно створить нову підписку.
            })
        }

        if (Notification.permission === 'denied') {
            console.warn('[WebPush] Notification permission denied by user')
            return
        }
        if (Notification.permission === 'default') {
            const result = await Notification.requestPermission()
            console.log('[WebPush] requestPermission →', result)
            if (result !== 'granted') return
        }

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
        })
        console.log('[WebPush] subscribed', subscription.endpoint)

        await sendSubscriptionToServer(subscription)
    } catch (e: any) {
        console.error('[WebPush] subscribeToPush failed', e?.name, e?.message, e)
    }
}

async function sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    const p256dhKey = subscription.getKey('p256dh')
    const authKey = subscription.getKey('auth')

    if (!p256dhKey || !authKey) {
        console.warn('[WebPush] subscription missing keys')
        return
    }

    const p256dh = btoa(String.fromCharCode(...new Uint8Array(p256dhKey)))
    const auth = btoa(String.fromCharCode(...new Uint8Array(authKey)))

    try {
        await restClient.post('/services/user/push-subscription', {
            json: {
                endpoint: subscription.endpoint,
                p256dh,
                auth,
            },
        })
        console.log('[WebPush] subscription saved on server')
    } catch (e: any) {
        console.error('[WebPush] failed to save subscription on server', e?.message, e)
    }
}

/**
 * Прибирає push-підписку цього пристрою — викликається перед виходом.
 *
 * НАВІЩО. Раніше підписка переживала logout: UserManager.clear() чистить
 * лише токени. На спільному телефоні наступний водій, увійшовши,
 * перевикористовував ту саму підписку (пошук на сервері йде за endpoint_hash),
 * і сервер перепризначав її новому власнику — push першого водія починав
 * приходити другому.
 *
 * Викликати ДО userManager.logout(): серверний запит потребує чинного токена.
 * Помилки навмисно ковтаються — невдале відписування не має блокувати вихід.
 */
export async function unsubscribeFromPush(): Promise<void> {
    try {
        await clearPushCredentials()

        if (!('serviceWorker' in navigator)) return

        const registration = await navigator.serviceWorker.getRegistration()
        const subscription = await registration?.pushManager.getSubscription()
        if (!subscription) return

        // Спершу повідомляємо сервер — поки токен ще чинний.
        try {
            await restClient.delete('/services/user/push-subscription', {
                json: { endpoint: subscription.endpoint },
                // restClient.delete не додає Content-Type за замовчуванням
                // (isNotHeaderAddContentType), тож ставимо явно.
                headers: { 'Content-Type': 'application/json' },
            })
        } catch (e: any) {
            console.warn('[WebPush] failed to delete subscription on server', e?.message)
        }

        await subscription.unsubscribe()
    } catch (e: any) {
        console.warn('[WebPush] unsubscribeFromPush failed', e?.message)
    }
}

export function ServiceWorkerRegistrar({ updateCheckInterval = 60 * 60 * 1000 }: ServiceWorkerRegistrarProps) {
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return

        let intervalId: ReturnType<typeof setInterval> | null = null

        const handleControllerChange = () => {
            pwaManager.setUpdateAvailable(true)
        }

        const handleUpdateFound = (registration: ServiceWorkerRegistration) => {
            const installing = registration.installing
            if (!installing) return

            installing.addEventListener('statechange', () => {
                if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                    pwaManager.setUpdateAvailable(true)
                    pwaManager.setWaitingWorker(registration.waiting)
                }
            })
        }

        navigator.serviceWorker.getRegistration().then((registration) => {
            if (!registration) return

            // Нова версія могла встановитись ще до монтування (застосунок висів у фоні
            // під час деплою) — тоді updatefound вже не прийде, і без цієї перевірки
            // водій не побачив би тост «Оновити» і лишився б на старому SW.
            if (registration.waiting && navigator.serviceWorker.controller) {
                pwaManager.setWaitingWorker(registration.waiting)
                pwaManager.setUpdateAvailable(true)
            }

            registration.addEventListener('updatefound', () => handleUpdateFound(registration))

            intervalId = setInterval(() => {
                if (navigator.onLine) {
                    registration.update().catch(() => {
                        // Silently ignore update check failures (offline, etc.)
                    })
                }
            }, updateCheckInterval)

            // Підписка на Web Push після того, як SW активний
            navigator.serviceWorker.ready.then((activeRegistration) => {
                subscribeToPush(activeRegistration)
            })
        })

        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)

        // Тримаємо облікові дані для SW актуальними. Токен ротується при
        // кожному оновленні (раз на кілька годин), а SW використовує його
        // для перепідписки при ротації endpoint — із протухлим токеном
        // перепідписка отримала б 401 і push зник би до наступного входу.
        const disposeTokenReaction = reaction(
            () => userManager.token,
            (token) => {
                if (token && VAPID_PUBLIC_KEY) {
                    savePushCredentials({ vapidPublicKey: VAPID_PUBLIC_KEY, token })
                }
            }
        )

        return () => {
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
            if (intervalId !== null) clearInterval(intervalId)
            disposeTokenReaction()
        }
    }, [updateCheckInterval])

    return null
}
